/**
 * i18n core. Enforces CSTS §5: no string literals in components — everything goes through t().
 *
 * DEV mode warns on missing keys. The CSTS three-layer gate lives in the CI test
 * (tests/unit/i18n-keys.test.ts + i18n-coverage.test.ts), not at runtime — a single
 * missing key should not blank the whole game.
 */
export class I18n {
  private locale = "en";
  private table: Record<string, string> = {};
  private tables = new Map<string, Record<string, string>>();
  private warned = new Set<string>();

  async loadLocale(code: string, url: string): Promise<void> {
    if (this.tables.has(code)) return;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`i18n: failed to load locale ${code}`);
    const json = (await res.json()) as Record<string, string>;
    this.tables.set(code, json);
    if (code === this.locale) this.table = { ...this.table, ...json };
  }

  setLocale(code: string): void {
    this.locale = code;
    const t = this.tables.get(code);
    if (t) this.table = { ...t };
  }

  currentLocale(): string {
    return this.locale;
  }

  t(key: string, fallback?: string): string {
    const value = this.table[key];
    if (value !== undefined) return value;
    if (import.meta.env?.DEV && !this.warned.has(key)) {
      this.warned.add(key);
      console.warn(`[i18n] missing key "${key}" in locale "${this.locale}"`);
    }
    return fallback ?? key;
  }
}
